import type { SelectedTopFilters } from '@/components/Hotels/types'
import INPUT_MIN_WIDTH from '@/constants/dropdown'
import type { LOCATION_SEARCH_TYPE } from '@/types'
import { type GetItemPropsOptions, type GetMenuPropsOptions, type GetPropsCommonOptions } from 'downshift'
import { Box, SrOnly } from 'pcln-design-system'
import { useCallback, useEffect, type Dispatch, type SetStateAction } from 'react'
import { type CurrentLocationMachineState } from '../../../machines/currentLocationMachine'
import { top20Filters } from '../constants'
import FilterBar from '../FilterBar/FilterBar'
import TypeaheadPenny from '../TypeaheadPenny'
import type { TypeAheadConfigEndpoint, TypeaheadFeedbackData } from '../types.ts'
import useTypeAhead from '../useTypeAhead'
import { cityIdExistsInTop20Filters } from '../utils'
import { DropdownCard } from './DropdownCard.styled'
import RenderCurrentLocation from './RenderCurrentLocation'
import RenderRecentSearches from './RenderRecentSearches'
import SearchItem from './SearchItem'

type DropdownProps = {
  isOpen: boolean
  highlightedIndex: number | null
  getItemProps: (options: GetItemPropsOptions<LOCATION_SEARCH_TYPE>) => Record<string, unknown>
  getMenuProps: (
    options?: GetMenuPropsOptions,
    otherOptions?: GetPropsCommonOptions,
  ) => Record<string, unknown>
  searchKey: string
  searchValue: string | null
  clearItems: () => void
  setHighlightedIndex: (index: number) => void
  showCurrentLocation?: boolean
  isMobile?: boolean
  current?: CurrentLocationMachineState
  handleRequestCurrentLocationClick?: () => void
  typeAheadEndpoint: TypeAheadConfigEndpoint
  recentSearches?: LOCATION_SEARCH_TYPE[]
  isTwoLineDisplay?: boolean
  recentOrRecommendationTitle: string
  nearbyAirportsFeatureEnabled?: boolean
  shouldDisplayPenny?: boolean
  setSelectedTopFilters?: (value: SetStateAction<SelectedTopFilters>) => void
  selectedTopFilters?: SelectedTopFilters
  setIsPopoverOpen: Dispatch<SetStateAction<boolean>>
  setTypeaheadFeedbackData: Dispatch<SetStateAction<TypeaheadFeedbackData>>
  showHotelTypeaheadFilters: boolean
  useNewTypeaheadIcons: boolean
}

function Dropdown({
  isOpen,
  highlightedIndex,
  getItemProps,
  getMenuProps,
  searchKey,
  searchValue,
  clearItems,
  current,
  handleRequestCurrentLocationClick,
  showCurrentLocation = false,
  setHighlightedIndex,
  isMobile = false,
  typeAheadEndpoint,
  recentSearches = [],
  recentOrRecommendationTitle,
  isTwoLineDisplay,
  shouldDisplayPenny,
  setSelectedTopFilters,
  selectedTopFilters,
  setIsPopoverOpen,
  setTypeaheadFeedbackData,
  showHotelTypeaheadFilters,
  useNewTypeaheadIcons,
}: Readonly<DropdownProps>) {
  const handleTypeaheadLoaded = useCallback(() => {
    clearItems()
    setHighlightedIndex(0)
  }, [clearItems, setHighlightedIndex])
  const { items, duration, source } = useTypeAhead({
    searchValue,
    searchKey,
    typeAheadEndpoint,
    showHotelTypeaheadFilters,
    onLoaded: handleTypeaheadLoaded,
  })

  useEffect(() => {
    setTypeaheadFeedbackData({ items, source, duration })
  }, [items, source, duration, setTypeaheadFeedbackData])

  if (!isOpen) {
    return null
  }

  const isCardVisible = isOpen && items.length > 0
  const isRecentSearchesVisible = isOpen && recentSearches.length > 0 && searchValue === ''

  const cityFilters =
    showHotelTypeaheadFilters && items.length > 0 && cityIdExistsInTop20Filters(items[0].cityID)
      ? top20Filters[items[0].cityID]
      : []

  return (
    <>
      {isCardVisible && <SrOnly aria-live='polite'>{items.length} number of results</SrOnly>}
      <DropdownCard
        boxShadowSize={isMobile ? undefined : 'overlay-lg'}
        data-testid='typeahead-dropdown-card'
        color='background.lightest'
        borderRadius={shouldDisplayPenny ? ['none', null, 'xl'] : 'xl'}
        mt={isMobile ? 0 : 1}
        role='listbox'
        rounded={isMobile ? 'bottom' : undefined}
        width={1}
        minWidth={isMobile ? '' : INPUT_MIN_WIDTH}
        visible={isCardVisible || isRecentSearchesVisible || showCurrentLocation}
        {...getMenuProps({
          refKey: 'ref',
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          isOpen,
        })}
      >
        <Box px={[3, null, 2]} py={2}>
          {showHotelTypeaheadFilters && (
            <FilterBar
              cityFilters={isMobile ? cityFilters.slice(0, 3) : cityFilters}
              setSelectedTopFilters={setSelectedTopFilters}
              selectedTopFilters={selectedTopFilters}
              setIsPopoverOpen={setIsPopoverOpen}
            />
          )}
          {showCurrentLocation && current && (
            <RenderCurrentLocation
              current={current}
              handleRequestCurrentLocationClick={handleRequestCurrentLocationClick}
            />
          )}
          {isRecentSearchesVisible ? (
            <RenderRecentSearches
              getItemProps={getItemProps}
              recentSearches={recentSearches}
              highlightedIndex={highlightedIndex}
              recentOrRecommendationTitle={recentOrRecommendationTitle}
              useNewTypeaheadIcons={useNewTypeaheadIcons}
            />
          ) : null}
          {isCardVisible
            ? items.map((item: LOCATION_SEARCH_TYPE, index: number) => (
                <SearchItem
                  isTwoLineDisplay={isTwoLineDisplay}
                  // adding index to key since typeahead service can sometimes return duplicate items

                  key={`${item.itemName}-${index}`}
                  isItemSelected={index === highlightedIndex}
                  index={index}
                  item={item}
                  getItemProps={getItemProps}
                  useNewTypeaheadIcons={useNewTypeaheadIcons}
                />
              ))
            : null}
        </Box>
        {shouldDisplayPenny && <TypeaheadPenny isRecentSearchesVisible={!!isRecentSearchesVisible} />}
      </DropdownCard>
    </>
  )
}

export default Dropdown
